import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Package, Plus, Trash, Warning, ClipboardText, Recycle, PencilSimple } from "@phosphor-icons/react";

const CATEGORIES = [
  { key: "food", label: "Food", color: "#E07A5F" },
  { key: "beverage", label: "Beverage", color: "#81B29A" },
  { key: "wine", label: "Wine", color: "#943126" },
  { key: "liquor", label: "Liquor", color: "#F4A261" },
  { key: "beer", label: "Beer", color: "#D4A437" },
  { key: "supply", label: "Supply", color: "#6D6875" },
];

const WASTE_REASONS = [
  { key: "spoilage", label: "Spoilage" },
  { key: "breakage", label: "Breakage" },
  { key: "comp", label: "Comp / Returned" },
  { key: "staff", label: "Staff Meal" },
  { key: "other", label: "Other" },
];

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("items");
  const [filterCat, setFilterCat] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showCount, setShowCount] = useState(null); // item being counted
  const [showWaste, setShowWaste] = useState(null); // item being wasted
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState({ name: "", category: "food", unit: "each", par_level: "", cost_per_unit: "", current_stock: "", supplier: "", notes: "" });
  const [countForm, setCountForm] = useState({ counted_quantity: "", notes: "" });
  const [wasteForm, setWasteForm] = useState({ quantity: "", reason: "spoilage", notes: "" });
  const [waste, setWaste] = useState([]);
  const [stockCounts, setStockCounts] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const params = filterCat ? `?category=${filterCat}` : "";
      const [i, s, w, c] = await Promise.all([
        api.get(`/inventory/items${params}`),
        api.get("/inventory/summary?days=30"),
        api.get("/inventory/waste?days=30"),
        api.get("/inventory/counts?days=30"),
      ]);
      setItems(i.data);
      setSummary(s.data);
      setWaste(w.data);
      setStockCounts(c.data);
    } catch { /* silent */ }
  }, [filterCat]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      ...form,
      par_level: parseFloat(form.par_level) || 0,
      cost_per_unit: parseFloat(form.cost_per_unit) || 0,
      current_stock: parseFloat(form.current_stock) || 0,
    };
    try {
      if (editingItem) {
        await api.put(`/inventory/items/${editingItem.id}`, payload);
        toast.success("Item updated");
      } else {
        await api.post("/inventory/items", payload);
        toast.success("Item added");
      }
      setShowAdd(false);
      setEditingItem(null);
      setForm({ name: "", category: "food", unit: "each", par_level: "", cost_per_unit: "", current_stock: "", supplier: "", notes: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item and all its history?")) return;
    try { await api.delete(`/inventory/items/${id}`); toast.success("Removed"); fetchAll(); }
    catch { toast.error("Failed"); }
  };

  const submitCount = async () => {
    if (!countForm.counted_quantity) { toast.error("Quantity required"); return; }
    try {
      await api.post("/inventory/counts", { item_id: showCount.id, counted_quantity: parseFloat(countForm.counted_quantity), notes: countForm.notes });
      toast.success("Count submitted");
      setShowCount(null);
      setCountForm({ counted_quantity: "", notes: "" });
      fetchAll();
    } catch { toast.error("Failed"); }
  };

  const submitWaste = async () => {
    if (!wasteForm.quantity) { toast.error("Quantity required"); return; }
    try {
      await api.post("/inventory/waste", { item_id: showWaste.id, quantity: parseFloat(wasteForm.quantity), reason: wasteForm.reason, notes: wasteForm.notes });
      toast.success("Waste logged");
      setShowWaste(null);
      setWasteForm({ quantity: "", reason: "spoilage", notes: "" });
      fetchAll();
    } catch { toast.error("Failed"); }
  };

  const editItem = (item) => {
    setEditingItem(item);
    setForm({
      name: item.name, category: item.category, unit: item.unit,
      par_level: String(item.par_level), cost_per_unit: String(item.cost_per_unit),
      current_stock: String(item.current_stock), supplier: item.supplier || "", notes: item.notes || "",
    });
    setShowAdd(true);
  };

  const lowStock = items.filter(i => parseFloat(i.par_level) > 0 && parseFloat(i.current_stock) <= parseFloat(i.par_level));

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="inventory-page">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Package size={24} weight="duotone" style={{ color: "#2F4F4F" }} />
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Inventory</h2>
          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Stock</Badge>
        </div>
        <Button onClick={() => { setEditingItem(null); setForm({ name: "", category: "food", unit: "each", par_level: "", cost_per_unit: "", current_stock: "", supplier: "", notes: "" }); setShowAdd(true); }} style={{ backgroundColor: "#2F4F4F" }} data-testid="add-item-btn">
          <Plus size={14} className="mr-1" /> Item
        </Button>
      </div>

      {/* KPI */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <KPI label="Total Items" value={summary.total_items} color="#2F4F4F" testId="kpi-items" />
          <KPI label="Inventory Value" value={`$${summary.total_inventory_value.toFixed(2)}`} color="#81B29A" testId="kpi-value" />
          <KPI label="Low Stock" value={summary.low_stock_count} color={summary.low_stock_count > 0 ? "#E07A5F" : "#A8A29E"} testId="kpi-low-stock" />
          <KPI label="Waste (30d)" value={`$${summary.waste_cost.toFixed(2)}`} color="#943126" sub={`${summary.waste_count} entries`} testId="kpi-waste" />
        </div>
      )}

      {/* Low-Stock Alert */}
      {lowStock.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 flex items-center gap-3" data-testid="low-stock-alert">
          <Warning size={20} weight="duotone" className="text-amber-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-amber-800">{lowStock.length} item{lowStock.length > 1 ? "s" : ""} at or below par level</p>
            <p className="text-[10px] text-amber-700">{lowStock.slice(0, 3).map(i => i.name).join(", ")}{lowStock.length > 3 ? `, +${lowStock.length - 3} more` : ""}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-stone-200">
        {[
          { key: "items", label: "Items" },
          { key: "counts", label: "Stock Counts" },
          { key: "waste", label: "Waste Log" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors ${tab === t.key ? "border-[#2F4F4F] text-[#2F4F4F]" : "border-transparent text-stone-400 hover:text-stone-600"}`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "items" && (
        <div className="bg-white rounded-lg border border-stone-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Items</h3>
            <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="text-xs border border-stone-200 rounded-md px-2 py-1 bg-white" data-testid="filter-category">
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          {items.length === 0 ? (
            <p className="text-center text-xs text-stone-400 py-6">No items yet. Add one to start tracking inventory.</p>
          ) : (
            <div className="space-y-1.5">
              {items.map((it) => {
                const cat = CATEGORIES.find(c => c.key === it.category) || CATEGORIES[CATEGORIES.length - 1];
                const isLow = parseFloat(it.par_level) > 0 && parseFloat(it.current_stock) <= parseFloat(it.par_level);
                return (
                  <div key={it.id} className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${isLow ? "bg-amber-50/50" : "hover:bg-stone-50"}`} data-testid={`item-${it.id}`}>
                    <div className="w-1 h-10 rounded-full" style={{ backgroundColor: cat.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-stone-800">{it.name}</span>
                        <Badge variant="outline" className="text-[9px]">{cat.label}</Badge>
                        {isLow && <Badge className="bg-amber-100 text-amber-700 text-[9px]">Low</Badge>}
                      </div>
                      <p className="text-[10px] text-stone-400">{it.supplier || "—"} · ${parseFloat(it.cost_per_unit).toFixed(2)} / {it.unit}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold" style={{ color: isLow ? "#E07A5F" : "#1C1917" }}>{parseFloat(it.current_stock).toFixed(1)} {it.unit}</p>
                      <p className="text-[10px] text-stone-400">par {parseFloat(it.par_level).toFixed(1)}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { setShowCount(it); setCountForm({ counted_quantity: String(it.current_stock), notes: "" }); }} className="h-7 px-2 text-xs" data-testid={`count-${it.id}`}>
                        <ClipboardText size={12} className="mr-1" /> Count
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setShowWaste(it); setWasteForm({ quantity: "", reason: "spoilage", notes: "" }); }} className="h-7 px-2 text-xs text-amber-600" data-testid={`waste-${it.id}`}>
                        <Recycle size={12} className="mr-1" /> Waste
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => editItem(it)} className="h-7 w-7 p-0 text-stone-500" data-testid={`edit-${it.id}`}>
                        <PencilSimple size={12} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(it.id)} className="h-7 w-7 p-0 text-red-400" data-testid={`delete-${it.id}`}>
                        <Trash size={12} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "counts" && (
        <div className="bg-white rounded-lg border border-stone-200 p-4">
          <h3 className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-3">Stock Count History</h3>
          {stockCounts.length === 0 ? (
            <p className="text-center text-xs text-stone-400 py-6">No counts logged yet.</p>
          ) : (
            <div className="space-y-1.5">
              {stockCounts.map((c) => (
                <div key={c.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-stone-50" data-testid={`count-row-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800">{c.item_name}</p>
                    <p className="text-[10px] text-stone-400">{new Date(c.counted_at).toLocaleString()} · {c.counted_by} {c.notes ? `· ${c.notes}` : ""}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">{c.counted_quantity}</p>
                    <p className={`text-[10px] ${c.delta > 0 ? "text-green-600" : c.delta < 0 ? "text-red-500" : "text-stone-400"}`}>
                      {c.delta > 0 ? "+" : ""}{c.delta.toFixed(1)} from {c.previous_stock.toFixed(1)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "waste" && (
        <div className="bg-white rounded-lg border border-stone-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Waste Log</h3>
            {summary && summary.waste_cost > 0 && (
              <span className="text-xs text-stone-500">Total cost (30d): <span className="font-semibold text-red-500">${summary.waste_cost.toFixed(2)}</span></span>
            )}
          </div>
          {waste.length === 0 ? (
            <p className="text-center text-xs text-stone-400 py-6">No waste logged in the last 30 days.</p>
          ) : (
            <div className="space-y-1.5">
              {waste.map((w) => (
                <div key={w.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-stone-50" data-testid={`waste-row-${w.id}`}>
                  <div className="w-1 h-10 rounded-full bg-red-300" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800">{w.item_name}</p>
                    <p className="text-[10px] text-stone-400">{new Date(w.logged_at).toLocaleString()} · {w.logged_by} · {w.reason}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-red-500">-{w.quantity}</p>
                    <p className="text-[10px] text-stone-500">${w.cost.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Item Dialog */}
      <Dialog open={showAdd} onOpenChange={(open) => { if (!open) { setShowAdd(false); setEditingItem(null); } }}>
        <DialogContent data-testid="add-item-dialog">
          <DialogHeader><DialogTitle>{editingItem ? "Edit Item" : "Add Inventory Item"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="item-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Category</Label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full border border-stone-200 rounded-md px-2 py-2 text-sm" data-testid="item-category">
                  {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Unit</Label>
                <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="mt-1 w-full border border-stone-200 rounded-md px-2 py-2 text-sm" data-testid="item-unit">
                  {["each", "lb", "kg", "oz", "bottle", "case", "gal"].map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Stock</Label>
                <Input type="number" step="0.1" value={form.current_stock} onChange={(e) => setForm({ ...form, current_stock: e.target.value })} className="mt-1" data-testid="item-stock" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Par</Label>
                <Input type="number" step="0.1" value={form.par_level} onChange={(e) => setForm({ ...form, par_level: e.target.value })} className="mt-1" data-testid="item-par" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Cost/Unit</Label>
                <Input type="number" step="0.01" value={form.cost_per_unit} onChange={(e) => setForm({ ...form, cost_per_unit: e.target.value })} className="mt-1" data-testid="item-cost" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Supplier</Label>
              <Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="mt-1" data-testid="item-supplier" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" data-testid="item-notes" />
            </div>
            <Button onClick={submit} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="save-item-btn">
              {editingItem ? "Save Changes" : "Add Item"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Count Dialog */}
      <Dialog open={!!showCount} onOpenChange={(open) => !open && setShowCount(null)}>
        <DialogContent data-testid="count-dialog">
          <DialogHeader><DialogTitle>Count: {showCount?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-stone-500">Current stock on file: <span className="font-semibold">{showCount?.current_stock} {showCount?.unit}</span></p>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Counted Quantity</Label>
              <Input type="number" step="0.1" value={countForm.counted_quantity} onChange={(e) => setCountForm({ ...countForm, counted_quantity: e.target.value })} className="mt-1" data-testid="count-quantity" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Notes</Label>
              <Input value={countForm.notes} onChange={(e) => setCountForm({ ...countForm, notes: e.target.value })} placeholder="e.g. Sunday count" className="mt-1" data-testid="count-notes" />
            </div>
            <Button onClick={submitCount} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="submit-count-btn">Submit Count</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Waste Dialog */}
      <Dialog open={!!showWaste} onOpenChange={(open) => !open && setShowWaste(null)}>
        <DialogContent data-testid="waste-dialog">
          <DialogHeader><DialogTitle>Log Waste: {showWaste?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <p className="text-xs text-stone-500">Stock will be reduced. Cost will be calculated at ${parseFloat(showWaste?.cost_per_unit || 0).toFixed(2)}/{showWaste?.unit}.</p>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Quantity Wasted</Label>
              <Input type="number" step="0.1" value={wasteForm.quantity} onChange={(e) => setWasteForm({ ...wasteForm, quantity: e.target.value })} className="mt-1" data-testid="waste-quantity" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Reason</Label>
              <select value={wasteForm.reason} onChange={(e) => setWasteForm({ ...wasteForm, reason: e.target.value })} className="mt-1 w-full border border-stone-200 rounded-md px-2 py-2 text-sm" data-testid="waste-reason">
                {WASTE_REASONS.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Notes</Label>
              <Input value={wasteForm.notes} onChange={(e) => setWasteForm({ ...wasteForm, notes: e.target.value })} className="mt-1" data-testid="waste-notes" />
            </div>
            <Button onClick={submitWaste} style={{ backgroundColor: "#943126" }} className="w-full" data-testid="submit-waste-btn">Log Waste</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, color, sub, testId }) {
  return (
    <div className="bg-white rounded-lg border border-stone-200 p-3" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">{label}</span>
      <p className="text-lg font-semibold mt-1" style={{ color }}>{value}</p>
      {sub && <p className="text-[10px] text-stone-400 mt-0.5">{sub}</p>}
    </div>
  );
}
